import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from "rxjs/operators";
import { AuthService } from 'src/app/auth/auth.service';

@Injectable()
export class ErrorCatchingInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  constructor(private readonly http: HttpClient, private readonly authService: AuthService) {
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        const isTokenExpired = error &&
          error.error &&
          error.error.code === 'token_not_valid';
        const isRefreshTokenExpired = error &&
        error.error &&
        error.error.code === 'token_not_valid' && 
        error.error.detail === 'Token is invalid or expired';

        if (error.status === 401 && isTokenExpired && !isRefreshTokenExpired) {
          return this.handle401Error(request, next);
        }

        // Log server-side exceptions (not already audited)
        if (error.url && error.url.indexOf('exception-log') === -1 && error.error && !error.error.audited) {
          const logBody = {
            url: error.url,
            payload: request.body,
            exception_type: error.status,
            exception_message: error.error.message ? error.error.message : error.message,
            exception_source: error.error.message ? 'Server' : 'Client',
            api_type: 'Internal'
          };
          this.http.post('api/master/exception-log', logBody).subscribe();
        }

        if (error.error && error.error.audited) {
          return throwError({ error: { message: 'Something went wrong' } });
        }

        return throwError(error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || '{}');
    const tokenInfo = JSON.parse(sessionStorage.getItem('tokenInfo') || '{}');
    const refreshToken = tokenInfo.refresh_token;

    if (!refreshToken || !userInfo.user_id) {
      this.authService.logout();
      return throwError('Missing refresh token or user ID');
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.http.post<any>('api/master/token/refresh/', { refresh: refreshToken }).pipe(
        switchMap((res) => {
          this.isRefreshing = false;
          tokenInfo.access_token = res.access;
          sessionStorage.setItem('tokenInfo', JSON.stringify(tokenInfo));
          this.refreshTokenSubject.next(res.access);

          const clonedRequest = request.clone({
            setHeaders: {
              Authorization: 'Bearer ' + res.access
            }
          });
          return next.handle(clonedRequest);
        }),
        catchError(err => {
          this.isRefreshing = false;
          this.authService.logout();
          return throwError(err);
        })
      );
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap((token) => {
          const clonedRequest = request.clone({
            setHeaders: {
              Authorization: 'Bearer ' + token
            }
          });
          return next.handle(clonedRequest);
        })
      );
    }
  }
}